import { useMutation, useQuery } from "@apollo/client";
import { useAbility } from "@casl/react";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";

import type { IAddFileValues } from "./add-files-modal/types";
import type { IAddFilesToDbResults, IFile, IUseFilesQueries } from "./types";

import { getEnvsFromResult } from "../git-roots/utils";
import {
  ADD_FILES_TO_DB_MUTATION,
  DOWNLOAD_FILE_MUTATION,
  GET_FILES,
  GET_GIT_ROOTS_QUERIES,
  GET_GROUP_ENV_URLS_FRAGMENT,
  REMOVE_FILE_MUTATION,
  SIGN_POST_URL_MUTATION,
} from "../queries";
import type { IEnvironmentUrlAttr } from "../types";
import { authzPermissionsContext } from "context/authz/config";
import { getFragmentData } from "gql/fragment-masking";
import type { GroupFile } from "gql/graphql";
import { useAudit } from "hooks/use-audit";
import { Logger } from "utils/logger";
import { msgError, msgSuccess } from "utils/notifications";
import { openUrl } from "utils/resource-helpers";
import { uploadGroupFileToS3 } from "utils/upload-file-to-s3";
import { sanitizeFileName } from "utils/validations";

const MIN_SUCCESS_STATUS_CODE = 200;
const MAX_SUCCESS_STATUS_CODE = 300;

const useFilesQueries = (
  closeAddModal: () => void,
  closeOptionsModal: () => void,
  fileName: string,
  groupName: string,
  organizationName: string,
  setValues: React.Dispatch<React.SetStateAction<IAddFileValues>>,
): IUseFilesQueries => {
  const permissions = useAbility(authzPermissionsContext);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [isAddingFileInfoToDB, setIsAddingFileInfoToDB] = useState(false);
  const { addAuditEvent } = useAudit();

  const { data, refetch } = useQuery(GET_FILES, {
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        msgError(t("groupAlerts.errorTextsad"));
        Logger.error("An error occurred loading group files", error);
      });
    },
    variables: { groupName },
  });
  const { data: gitRootsData } = useQuery(GET_GIT_ROOTS_QUERIES, {
    fetchPolicy: "cache-first",
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.error("Couldn't load Git roots environments", error);
      });
    },
    variables: {
      canGetSecrets: permissions.can(
        "integrates_api_resolvers_git_environment_url_secrets_resolve",
      ),
      groupName,
      organizationName,
    },
  });
  const [downloadFile] = useMutation(DOWNLOAD_FILE_MUTATION, {
    onCompleted: (downloadData): void => {
      openUrl(downloadData.downloadFile.url);
      addAuditEvent("Group.Files.Download", fileName);
    },
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        msgError(t("groupAlerts.errorTextsad"));
        Logger.error("An error occurred downloading group files", error);
      });
    },
    variables: { filesData: fileName, groupName },
  });
  const [removeFile] = useMutation(REMOVE_FILE_MUTATION, {
    onCompleted: (): void => {
      void refetch();
      msgSuccess(
        t("searchFindings.tabResources.successRemove"),
        t("searchFindings.tabUsers.titleSuccess"),
      );
    },
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        msgError(t("groupAlerts.errorTextsad"));
        Logger.error("An error occurred removing group files", error);
      });
    },
  });
  const [uploadFile] = useMutation(SIGN_POST_URL_MUTATION, {
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        if (error.message === "Exception - Invalid characters in filename") {
          msgError(t("searchFindings.tabResources.invalidChars"));
        } else {
          msgError(t("groupAlerts.errorTextsad"));
          Logger.error("An error occurred uploading group files", error);
        }
      });
    },
    variables: { filesDataInput: [{ description: "", fileName }], groupName },
  });

  const [addFilesToDb, { loading }] = useMutation(ADD_FILES_TO_DB_MUTATION, {
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        if (error.message === "File name already exists in group files") {
          msgError(t("validations.invalidFileName"));
        } else {
          msgError(t("groupAlerts.errorTextsad"));
          Logger.error("An error occurred adding files to the db", error);
        }
      });
    },
    variables: {
      filesDataInput: [
        { description: "", fileName, uploadDate: new Date().toISOString() },
      ],
      groupName,
    },
  });

  const envUrls = getFragmentData(GET_GROUP_ENV_URLS_FRAGMENT, gitRootsData);
  const gitEnvironmentUrls = useMemo(
    (): IEnvironmentUrlAttr[] => getEnvsFromResult(envUrls),
    [envUrls],
  );
  const apkEnvironments = useMemo(
    (): Record<string, IEnvironmentUrlAttr> =>
      gitEnvironmentUrls
        .filter((environment): boolean => environment.urlType === "APK")
        .reduce(
          (previous, current): Record<string, IEnvironmentUrlAttr> => ({
            [current.url]: current,
            ...previous,
          }),
          {},
        ),
    [gitEnvironmentUrls],
  );
  const resourcesFiles = useMemo((): GroupFile[] => {
    if (!data?.resources) {
      return [];
    }

    return data.resources.files ?? [];
  }, [data?.resources]);

  const filesDataset = useMemo((): IFile[] => {
    return resourcesFiles.map((file): IFile => {
      return { ...file, id: crypto.randomUUID() } as IFile;
    });
  }, [resourcesFiles]);

  const handleRemoveFile = useCallback(async (): Promise<void> => {
    closeOptionsModal();
    await removeFile({
      variables: { filesDataInput: { fileName }, groupName },
    });
  }, [closeOptionsModal, fileName, groupName, removeFile]);

  const handleUpload = useCallback(
    async (values: IAddFileValues): Promise<void> => {
      setValues(values);

      const sanitizedFileName = sanitizeFileName(values.file[0].name);
      const newFile = new File([values.file[0]], sanitizedFileName, {
        type: values.file[0].type,
      });
      const repeatedFiles = filesDataset.filter(
        (file): boolean => file.fileName === sanitizedFileName,
      );
      if (repeatedFiles.length > 0) {
        msgError(t("searchFindings.tabResources.repeatedItem"));

        return;
      }

      const firstFile = {
        description: values.description,
        fileName: sanitizedFileName,
        uploadDate: new Date().toISOString(),
      };

      const results = await uploadFile({
        onCompleted: (): void => {
          addAuditEvent(
            "UploadGroupFile",
            [firstFile.fileName, groupName].join("/"),
          );
        },
        variables: { filesDataInput: [firstFile], groupName },
      });

      if (!results.data) return;

      const onError = (): void => {
        closeAddModal();
      };

      try {
        const status = await uploadGroupFileToS3(
          results,
          newFile,
          groupName,
          onError,
          setProgressPercentage,
        );

        if (
          status < MIN_SUCCESS_STATUS_CODE ||
          status >= MAX_SUCCESS_STATUS_CODE
        ) {
          msgError(t("groupAlerts.errorTextsad"));

          return;
        }

        setIsAddingFileInfoToDB(true);
        await addFilesToDb({
          onCompleted: (resultData: IAddFilesToDbResults): void => {
            const { addFilesToDb: mutationResults } = resultData;
            const { success } = mutationResults;

            if (!success) {
              msgError(t("groupAlerts.errorTextsad"));
              Logger.error("An error occurred adding group files to the db");
              setIsAddingFileInfoToDB(false);
              closeAddModal();

              return;
            }
            msgSuccess(
              t("searchFindings.tabResources.success"),
              t("searchFindings.tabUsers.titleSuccess"),
            );
            void refetch();
          },
          variables: { filesDataInput: [firstFile], groupName },
        });
      } catch (err) {
        Logger.error("Error uploading file to s3", err);
      }

      setIsAddingFileInfoToDB(false);
      closeAddModal();
    },
    [
      addAuditEvent,
      setValues,
      filesDataset,
      groupName,
      closeAddModal,
      refetch,
      uploadFile,
      addFilesToDb,
    ],
  );

  return {
    apkEnvironments,
    data,
    downloadFile,
    filesDataset,
    handleRemoveFile,
    handleUpload,
    isAddingFileInfoToDB,
    isUploading: loading,
    progressPercentage,
  };
};

export { useFilesQueries };
